import React, { useState, useEffect } from 'react';
import { Button, Table, Modal, Typography, Space, Popconfirm, Input } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import AxiosCaller from '../../../../authenticatie/AxiosCaller';
import { Trash2, Pencil } from 'lucide-react';
import { openNotificationSuccess, openNotificationError } from '../../../../Components/Notification/Notification';

const { Title } = Typography;

const ProductenOverzichtScreen = () => {
  const { get, del } = AxiosCaller();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updateProducts, setUpdateProducts] = useState(false);
  const [filterNaam, setFilterNaam] = useState('');
  const [leverancierFilter, setLeverancierFilter] = useState([]); // to hold the available suppliers for filtering

  // Fetch Products with optional name filter and leverancier filter
  useEffect(() => {
    fetchProducts();
  }, [filterNaam, updateProducts]);

  const fetchProducts = async () => {
    try {
      const response = filterNaam
        ? await get(`/product/search/${filterNaam}`)
        : await get('/product');

      if (!response.data.length) {
        setProducts([]);
      } else {
        // Sort the products alphabetically by naam
        const sortedProducts = response.data.sort((a, b) => a.naam.localeCompare(b.naam));
        setProducts(sortedProducts);

        // Set the available suppliers for filtering (unique suppliers)
      // Extract unique suppliers only if they change
      const newSuppliers = Array.from(new Set(response.data.map((product) => product.leverancier.naam)));

      // Prevent unnecessary re-renders by only updating state if new suppliers are different
      setLeverancierFilter((prev) => (JSON.stringify(prev) === JSON.stringify(newSuppliers) ? prev : newSuppliers));
      }
    } catch (error) {
      // Use Modal.error to show the error message
     openNotificationError("Fout bij ophalen van prodcuten", error)
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (product) => {
   
    try {
      await del(`/product/${product.productData._id}`);
      openNotificationSuccess("Product succesvol verwijderd")
      setUpdateProducts(!updateProducts);
    } catch (error) {
      // Show error modal when remove fails
     console.error(error)
     openNotificationError("Fout bij het verwijderen van het product", error)
    }
  };

  const handleEdit = (product) => {
    navigate('/Admin/configuratie/MakenAanpassenProduct', { state: product.productData });
  };

  const columns = [
    {
      title: 'Product',
      dataIndex: 'naam',
      key: 'naam',
      sorter: (a, b) => a.naam.localeCompare(b.naam),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Hoeveelheid',
      dataIndex: 'hoeveelheid',
      key: 'hoeveelheid',
    },
    {
      title: 'OmzettingsFactor',
      dataIndex: 'omzettingsFactor',
      key: 'omzettingsFactor',
    },
    {
      title: 'OmzettingsEenheid',
      dataIndex: 'omzettingsEenheid',
      key: 'omzettingsEenheid',
    },
    {
      title: 'Leverancier',
      dataIndex: 'leverancier',
      key: 'leverancier',
      filters: leverancierFilter.map((leverancier) => ({
        text: leverancier,
        value: leverancier,
      })),
      onFilter: (value, record) => record.leverancier.includes(value),
      sorter: (a, b) => a.leverancier.localeCompare(b.leverancier),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Acties',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {/* Edit Button */}
          <Button
            type="link"
            icon={<Pencil style={{ color: 'var(--primary-color)', fontSize: '18px' }} />}
            onClick={() => handleEdit(record)}
            style={{ fontSize: '18px' }}
          />
          {/* Delete Button with Popconfirm */}
          <Popconfirm
            title={`Weet je zeker dat je '${record.naam}' wilt verwijderen?`}
            onConfirm={() => handleRemove(record)}
            okText="Ja"
            cancelText="Nee"
          >
            <Trash2 className="delete-button" style={{ fontSize: '18px', color: 'var(--primary-color)' }} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Transforming the product data, but still keeping the full product object
  const dataWithActions = products.map((product) => ({
    key: product._id,
    naam: product.naam,
    hoeveelheid: `${product.minimumHoeveelheid} ${product.eenheid?.naam}`, // Formatting for display
    omzettingsFactor: 1 / product.omzetting.factor, // Calculating and displaying the omzettings factor
    omzettingsEenheid: product.omzetting.eenheid?.naam, // Using the omzettingsEenheid name for display
    leverancier: product.leverancier.naam, // Displaying the supplier name
    productData: product, // Adding the full product object to the row (no transformation)
  }));

  // Search input handler
  const onSearch = (value) => {
    setFilterNaam(value); // Set search term for filtering
  };

  return (
    <div className="Screen-container">
      <div className="space-between">
        {/* Title on the Left */}
        <h1 className='title'>Producten</h1>

        {/* Search Field on the Right */}
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Input
            placeholder="Zoek op productnaam"
            onChange={(e) => onSearch(e.target.value)} // Trigger search on text input
            style={{ width: 300, marginRight: 16 }} // Set the width and margin for the search field
          />

          {/* Add Product Button */}
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/Admin/configuratie/MakenAanpassenProduct')}
            className="button"
          >
            Nieuw Product
          </Button>
        </div>
      </div>

      <Table
        dataSource={dataWithActions}
        columns={columns}
        loading={loading}
        rowClassName={(record, index) =>
          index % 2 === 0 ? 'custom-table-row-even' : 'custom-table-row-odd'
        }
        bordered
        scroll={{ y: 0.5 * window.innerHeight }} // Keep table scrollable
        pagination={false} // Disable pagination
        defaultSortOrder="ascend" // Sort by default
      />
    </div>
  );
};

export default ProductenOverzichtScreen;
